import React from "react";
import DayHeaderCell from "../molecules/DayHeaderCell";
import RdvBlock from "../molecules/RdvBlock";
import AllDayEventChip from "../atoms/AllDayEventChip";
import { LINE, BG } from "../../Bureau/bureauTokens";

// 0–24 plutôt que la plage "heures ouvrées" de la maquette (7–21) : les vrais
// événements Google Calendar peuvent tomber à n'importe quelle heure — hors
// de cette plage, un bloc se positionnait hors de la zone visible de sa
// colonne (ni erreur ni log, juste invisible). Les RDV de démo restent tous
// dans une fourchette raisonnable donc rien ne change visuellement pour eux.
const START_H = 0;
const END_H = 24;
const HOUR_H = 46;
const NOW_RED = "#E5484D";

function timeToMin(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}
function evTop(t) {
  return ((timeToMin(t) - START_H * 60) / 60) * HOUR_H;
}
function evHeight(dur) {
  return Math.max(24, ((dur || 30) / 60) * HOUR_H);
}
function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// .pl-cal — grille hebdomadaire : en-tête des 7 jours + colonne d'heures +
// 7 colonnes jour avec lignes horaires, trame jour férié, ligne "maintenant"
// et blocs de RDV (tous Google Calendar) positionnés en absolu. Cliquer un
// créneau vide ouvre "Nouveau RDV" pré-rempli à cette date/heure (arrondie
// au quart d'heure le plus proche) — pas de glisser-déposer/étirement pour
// autant (voir plan Planning). `blockedRangesByDay` (facultatif) grise une
// plage horaire (indisponibilité récurrente) en fond, sans bloquer le clic.
function WeekCalendarGrid({ days, today, now, rdvsByDay, allDayEventsByDay, holidayOf, onOpenRdv, onOpenAllDay, onSlotClick, blockedRangesByDay }) {
  const hours = Array.from({ length: END_H - START_H }, (_, i) => START_H + i);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nowTop = ((nowMin - START_H * 60) / 60) * HOUR_H;
  const showNow = nowMin >= START_H * 60 && nowMin <= END_H * 60;
  const bodyHeight = hours.length * HOUR_H;

  const hasAllDay = !!allDayEventsByDay && days.some((d) => allDayEventsByDay(d).length > 0);

  const handleColumnClick = (e, d) => {
    if (!onSlotClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const rawMin = START_H * 60 + ((e.clientY - rect.top) / HOUR_H) * 60;
    const snapped = Math.round(rawMin / 15) * 15;
    const clamped = Math.max(START_H * 60, Math.min(END_H * 60 - 15, snapped));
    const hh = String(Math.floor(clamped / 60)).padStart(2, "0");
    const mm = String(clamped % 60).padStart(2, "0");
    onSlotClick(isoOf(d), `${hh}:${mm}`);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ border: `1px solid ${LINE}`, borderRadius: 16, overflow: "hidden", background: "#fff", boxShadow: "0 1px 0 rgba(31,22,32,.04), 0 1px 2px rgba(31,22,32,.04)" }}>
      <div className="grid flex-shrink-0" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", borderBottom: `1px solid ${LINE}`, background: `linear-gradient(180deg, #fff, ${BG})`, zIndex: 5 }}>
        <div style={{ borderRight: `1px solid ${LINE}` }} />
        {days.map((d) => (
          <DayHeaderCell key={isoOf(d)} date={d} isToday={isoOf(d) === isoOf(today)} holiday={holidayOf(d)} />
        ))}
      </div>

      {hasAllDay && (
        <div className="grid flex-shrink-0" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", borderBottom: `1px solid ${LINE}`, background: "#fff" }}>
          <div className="flex items-center justify-end text-[9px] font-semibold" style={{ borderRight: `1px solid ${LINE}`, color: "#8A7780", padding: "4px 8px" }}>
            Journée
          </div>
          {days.map((d) => (
            <div key={isoOf(d)} className="flex flex-col gap-0.5" style={{ borderLeft: `1px solid ${LINE}`, padding: 3 }}>
              {allDayEventsByDay(d).map((e) => (
                <AllDayEventChip key={e.id} title={e.title} color={e.color} onClick={() => onOpenAllDay(e)} />
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="grid flex-1 min-h-0 overflow-y-auto relative" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div style={{ borderRight: `1px solid ${LINE}`, background: BG }}>
          {hours.map((h) => (
            <div key={h} className="relative" style={{ height: HOUR_H }}>
              <span className="absolute text-[10px] font-semibold" style={{ top: -7, right: 8, color: "#8A7780", whiteSpace: "nowrap" }}>
                {h}:00
              </span>
            </div>
          ))}
        </div>
        {days.map((d) => {
          const isToday = isoOf(d) === isoOf(today);
          const hol = holidayOf(d);
          const rdvs = rdvsByDay(d);
          return (
            <div
              key={isoOf(d)}
              className="relative"
              onClick={hol ? undefined : (e) => handleColumnClick(e, d)}
              style={{
                borderLeft: `1px solid ${LINE}`,
                height: bodyHeight,
                cursor: hol ? "default" : "pointer",
                background: hol
                  ? "repeating-linear-gradient(-45deg, rgba(110,26,54,.045), rgba(110,26,54,.045) 8px, rgba(110,26,54,.02) 8px, rgba(110,26,54,.02) 16px)"
                  : isToday
                  ? "rgba(110,26,54,.025)"
                  : "transparent",
              }}
            >
              {hours.map((h) => (
                <div key={h} className="relative box-border" style={{ height: HOUR_H, borderBottom: `1px solid ${LINE}` }}>
                  <div className="absolute left-0 right-0" style={{ top: "50%", borderTop: `1px dashed ${LINE}`, opacity: 0.45 }} />
                </div>
              ))}

              {!hol && blockedRangesByDay &&
                blockedRangesByDay(d).map((b, i) => (
                  <div
                    key={i}
                    className="absolute left-0 right-0"
                    title={b.motif}
                    style={{
                      top: evTop(b.heureDebut),
                      height: evHeight((timeToMin(b.heureFin) - timeToMin(b.heureDebut))),
                      background: "repeating-linear-gradient(-45deg, rgba(31,22,32,.05), rgba(31,22,32,.05) 8px, rgba(31,22,32,.02) 8px, rgba(31,22,32,.02) 16px)",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                ))}

              {hol && (
                <div
                  className="absolute flex items-center gap-1 text-[9px] font-bold truncate"
                  style={{
                    top: 4,
                    left: 4,
                    right: 4,
                    zIndex: 3,
                    color: "#6E1A36",
                    background: "rgba(255,255,255,.85)",
                    border: "1px solid #F7E9EE",
                    borderRadius: 6,
                    padding: "3px 6px",
                    pointerEvents: "none",
                  }}
                >
                  {hol}
                </div>
              )}

              {isToday && showNow && (
                <div className="absolute left-0 right-0" style={{ top: nowTop, height: 0, borderTop: `2px solid ${NOW_RED}`, zIndex: 4, pointerEvents: "none" }}>
                  <span className="absolute" style={{ left: -4, top: -5, width: 8, height: 8, borderRadius: "50%", background: NOW_RED }} />
                </div>
              )}

              {rdvs.map((r) => (
                <RdvBlock
                  key={r.id}
                  top={evTop(r.time)}
                  height={evHeight(r.dur)}
                  time={r.time}
                  canal={r.canal}
                  client={r.client}
                  motif={r.motif}
                  collabInitials={r.collabInitials || "G"}
                  color={r.color}
                  onClick={() => onOpenRdv(r)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WeekCalendarGrid;
